import Spreadsheet from '@/components/UI/SpreadSheet/Spreadsheet';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type { Animal } from '@/model/Animal.model';
import type { MetadataField } from '@/model/Metadata.model';
import type { BulkSamplesFormData, SampleData } from '@/model/Sample.model';
import { type FC, type MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { TimepointEditor, timepointAfterPasteHandler } from '@/components/UI/SpreadSheet/editors/TimepointEditor';
import { DateTimeEditor } from '@/components/UI/SpreadSheet/editors/DateTimeEditor';
import { defaultMetadataToColumnSettingMapper } from '@/support/hot';
import { generateColumns, generateSampleData } from './BulkCreateSamples.utils';

TimepointEditor.register();
DateTimeEditor.register();

interface ViewBulkSampleDetailsTableProps {
  subjects: Array<Animal>;
  metadata: Array<MetadataField>;
  setInvalid: (invalid: boolean) => void;
  sampleDetails: BulkSamplesFormData;
  plannedSamplesEnabled: boolean;
  dataRef: MutableRefObject<Array<SampleData>>;
  onReady: (ready: boolean) => void;
}

const ViewBulkSampleDetailsTable: FC<ViewBulkSampleDetailsTableProps> = ({
  subjects,
  metadata,
  setInvalid,
  sampleDetails,
  plannedSamplesEnabled,
  dataRef,
  onReady,
}) => {
  const data = useMemo(
    () => generateSampleData(subjects, metadata, sampleDetails),
    [subjects, metadata, sampleDetails]
  );
  dataRef.current = data;

  useEffect(() => {
    onReady(true);
    return () => {
      onReady(false);
    };
  }, [onReady]);

  const { colHeaders, columns } = useMemo(
    () => generateColumns(sampleDetails, metadata, plannedSamplesEnabled),
    [sampleDetails, metadata, plannedSamplesEnabled]
  );
  const ref = useRef<HotTableRef>(null);

  useEffect(() => {
    const hot = ref.current?.hotInstance;
    if (_notNil(hot)) {
      hot.addHook('afterPaste', timepointAfterPasteHandler);

      return () => {
        try {
          if (!hot.isDestroyed) {
            hot.removeHook('afterPaste', timepointAfterPasteHandler);
          }
        } catch (error) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to remove timepoint paste hook during cleanup', {
              cause: error,
              slug: 'view-bulk-sample-details-hot-cleanup-error',
            })
          );
        }
      };
    }
  }, [ref]);

  const settings: SpreadsheetSettings = {
    className: 'htMiddle',
    colHeaders,
    columnSorting: false,
    columns,
    contextMenu: false,
    height: 500,
    hiddenColumns: { copyPasteEnabled: true, columns: [] },
    manualColumnResize: true,
    rowHeaders: true,
    rowHeights: 50,
    fixedColumnsStart: 3,
  };

  return (
    <Spreadsheet
      data={data}
      settings={settings}
      metadata={metadata}
      metadataToColumnSettingMapper={defaultMetadataToColumnSettingMapper}
      innerRef={ref}
      maxRows={subjects.length}
      addCol={false}
      setInvalid={setInvalid}
    />
  );
};

export default ViewBulkSampleDetailsTable;
